import { useTranslation } from 'react-i18next'
import { MultiFiltro } from '@/components/common/MultiFiltro'
import {
  BarraFiltrosActivos, descripcionDe, filtrosDe, type FiltroActivo,
} from '@/components/common/BarraFiltrosActivos'
import { etiquetaCatalogoDe } from '@/utils/filtroArticulo'
import { filterFieldCls, filterLabelCls, filterCardCls } from '@/components/common/FilterBar'
import { DateRangePicker } from '@/components/common/DateRangePicker'

interface Props {
  dark: boolean
  desde: string
  hasta: string
  q: string[]
  /** Catálogo de componentes consumidos, para sugerir al escribir. */
  opcionesComponente?: { valor: string; titulo: string; detalle?: string }[]
  familia: string
  familias: string[]
  showEmpresaFilter: boolean
  empresa: string
  allowedEmpresas: string[]
  setDesde: (v: string) => void
  setHasta: (v: string) => void
  setQ: (v: string[]) => void
  /** Feedback de resultados, ya formateado por la página. */
  resumen?: string
  setFamilia: (v: string) => void
  setEmpresa: (v: string) => void
}

/** Fecha ISO a dd/mm/aaaa para el chip. Un guion cuando el extremo está abierto. */
function fechaCorta(iso: string): string {
  return iso ? iso.split('-').reverse().join('/') : '…'
}

export function ConsumosFilters({
  dark, desde, hasta, q, opcionesComponente = [], resumen, familia, familias, showEmpresaFilter, empresa, allowedEmpresas,
  setDesde, setHasta, setQ, setFamilia, setEmpresa,
}: Props) {
  const { t } = useTranslation()
  const labelCls = filterLabelCls(dark)
  // Misma franja que el resto del producto: sin ella, Consumos era la única
  // pantalla donde no se veía qué había seleccionado.
  const filtrosActivos: FiltroActivo[] = [
    // El rango va como un chip más, y no sólo dentro de su control. Ahora que los
    // filtros sobreviven al cambio de pantalla, volver con un rango puesto y sin
    // rastro de él en la franja dejaba la lista corta sin explicación —y sin
    // «Limpiar todos», que sólo se pinta cuando hay algún chip.
    ...(desde || hasta
      ? [{
          clave: 'fechas', campo: t('common.filtros.fechas'),
          valor: [fechaCorta(desde), fechaCorta(hasta)].join(' – '),
          quitar: () => { setDesde(''); setHasta('') },
        }]
      : []),
    // El chip repite el rótulo del desplegable —código formateado— mientras el
    // valor que filtra sigue siendo el crudo del catálogo.
    ...filtrosDe(q, setQ, {
      campo: t('common.filtros.componente'),
      etiquetaDe: etiquetaCatalogoDe(opcionesComponente),
      tooltipDe: descripcionDe(opcionesComponente),
    }),
    ...(familia ? [{
      clave: 'fam', campo: t('common.family'), valor: familia,
      quitar: () => setFamilia(''),
    }] : []),
    ...(empresa ? [{
      clave: 'emp', campo: t('common.company'), valor: empresa.toUpperCase(),
      quitar: () => setEmpresa(''),
    }] : []),
  ]

  return (
    <div className={filterCardCls(dark)}>
    <div className="p-3 flex flex-wrap gap-3 items-end">
      <DateRangePicker dark={dark} desde={desde} hasta={hasta} onDesde={setDesde} onHasta={setHasta} />
      <div className="flex flex-col gap-1 min-w-[200px] flex-1">
        <label className={labelCls}>{t('production.consumoInvertido.searchComponent')}</label>
        <MultiFiltro
          dark={dark}
          permitirLibre
          opciones={opcionesComponente}
          seleccion={q}
          onSeleccion={setQ}
          placeholder={t('production.consumoInvertido.searchPlaceholder')}
        />
      </div>
      <div className="flex flex-col gap-1 min-w-[160px]">
        <label className={labelCls}>{t('production.consumoInvertido.family')}</label>
        <select value={familia} onChange={e => setFamilia(e.target.value)} className={`${filterFieldCls(dark, !!familia)} cursor-pointer`}>
          <option value="">{t('production.consumoInvertido.allFamilies')}</option>
          {familias.map(f => <option key={f} value={f}>{f}</option>)}
        </select>
      </div>
      {showEmpresaFilter && (
        <div className="flex flex-col gap-1 min-w-[90px]">
          <label className={labelCls}>{t('common.company')}</label>
          <select value={empresa} onChange={e => setEmpresa(e.target.value)} className={`${filterFieldCls(dark, !!empresa)} cursor-pointer`}>
            <option value="">{t('common.allCompanies')}</option>
            {allowedEmpresas.map(e => <option key={e} value={e}>{e.toUpperCase()}</option>)}
          </select>
        </div>
      )}
    </div>

    {filtrosActivos.length > 0 && (
      <div className="px-3 pb-3 -mt-1">
        <BarraFiltrosActivos
          dark={dark}
          filtros={filtrosActivos}
          onLimpiar={() => { setQ([]); setFamilia(''); setEmpresa('') }}
          resumen={resumen}
        />
      </div>
    )}
    </div>
  )
}
